import debounce from './debounce';

const VISIBILITY_THRESHOLD = 0.6;
const DEBOUNCE_TIMEOUT_MS = 500;
const ANALYTICS_ATTRIBUTE = 'data-card-analytics-options';
const ANALYTICS_RULE = `[${ANALYTICS_ATTRIBUTE}]`;
const CARD_LINK_RULE = '.Card-inner, .HorizontalCard-inner';
// gallery arrows and bullets are inside the card link but don't open the page
const IGNORE_CLICK_RULE = '[data-ignore-click]';
// cards with different scopes (e.g. listing and map) are counted separately
const SCOPE_ATTRIBUTE = 'data-card-analytics-scope';

const parseOptions = (card) => {
  try {
    return JSON.parse(card.getAttribute(ANALYTICS_ATTRIBUTE));
  } catch (error) {
    console.warn(error);
    return null;
  }
};

const getEventAction = (card) => {
  const link = card.querySelector(CARD_LINK_RULE);
  return link ? link.getAttribute('data-event-action') : undefined;
};

const sendListEvent = ({ eventLabel, listId, listName, items }) => {
  gtag('event', eventLabel, {
    currency: 'usd',
    route_to_new_ga4: true,
    item_list_id: listId,
    ...(listName && { item_list_name: listName }),
    items,
  });
};

class CardAnalytics {
  constructor() {
    // key: `${item_list_id}|${item_id}` — the same card can be shown in several lists
    this.seenItems = new Set();
    // key: item_list_id
    this.pendingLists = new Map();

    this.visibilityObserver = null;
    this.domObserver = null;

    if (!('IntersectionObserver' in window)) {
      return;
    }

    this.attachListeners();
  }

  attachListeners = () => {
    this.visibilityObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;

          const card = entry.target;
          this.visibilityObserver.unobserve(card);
          this.addPendingItem(card);
        });

        this.sendViewItemListDebounced();
      },
      { threshold: VISIBILITY_THRESHOLD },
    );

    // observe dynamically added cards
    this.domObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        Array.from(mutation.addedNodes).forEach((node) => {
          if (!(node instanceof Element)) return;

          if (node.matches(ANALYTICS_RULE)) {
            this.observeCard(node);
          }

          node.querySelectorAll(ANALYTICS_RULE).forEach(this.observeCard);
        });
      });
    });

    this.domObserver.observe(document.body, { childList: true, subtree: true });

    document.querySelectorAll(ANALYTICS_RULE).forEach(this.observeCard);

    document.addEventListener('click', this.handleClick, { capture: true });
  };

  getItemKey = (card, { item_list_id: listId, item_id: itemId }) =>
    `${card.getAttribute(SCOPE_ATTRIBUTE) || ''}|${listId}|${itemId}`;

  observeCard = (card) => {
    const options = parseOptions(card);
    if (!options || this.seenItems.has(this.getItemKey(card, options))) {
      return;
    }

    this.visibilityObserver.observe(card);
  };

  addPendingItem = (card) => {
    const options = parseOptions(card);
    if (!options) return;

    const key = this.getItemKey(card, options);
    if (this.seenItems.has(key)) return;
    this.seenItems.add(key);

    const { item_list_id: listId, item_list_name: listName, ...item } = options;
    const list = this.pendingLists.get(listId) || {
      listId,
      listName,
      eventAction: getEventAction(card),
      items: [],
    };

    list.items.push(item);
    this.pendingLists.set(listId, list);
  };

  sendViewItemListDebounced = debounce(() => {
    this.pendingLists.forEach((list) => {
      sendListEvent({ ...list, eventLabel: 'view_item_list' });
    });
    this.pendingLists.clear();
  }, DEBOUNCE_TIMEOUT_MS);

  handleClick = (event) => {
    if (event.target.closest(IGNORE_CLICK_RULE)) return;

    const link = event.target.closest(CARD_LINK_RULE);
    if (!link) return;

    const card = link.closest(ANALYTICS_RULE);
    if (!card) return;

    const options = parseOptions(card);
    if (!options) return;

    const { item_list_id: listId, item_list_name: listName, ...item } = options;

    sendListEvent({
      eventLabel: 'select_item',
      listId,
      listName,
      items: [item],
    });
  };
}

export default CardAnalytics;
